/* ============================================================
   assets/apex-spectrum-bar.js
   PHASE 6 · Workstream B — flat color-spectrum bar behavior.
   Progressive enhancement: upgrades <apex-spectrum-bar> (hidden
   by default), hides the tag-link swatch fallback, and filters
   the already-rendered grid client-side by data-colorway.
   Park the indicator: colorways whose center lies within one
   slot of the indicator stay visible (1–2 "nearby" colors).
   Keyboard: the thumb is a slider — arrows snap between
   colorways, Home/End jump, Escape or "Show all" resets.
   No dependencies. ~2.5KB. Collection template only.
   ============================================================ */
(function () {
  'use strict';
  if (customElements.get('apex-spectrum-bar')) return;

  class ApexSpectrumBar extends HTMLElement {
    connectedCallback() {
      try {
        this.items = JSON.parse(this.querySelector('.apex-spectrum__data').textContent);
      } catch (e) {
        return; // malformed data: stay hidden, fallback row remains
      }
      if (!this.items || this.items.length < 2) return;

      this.track = this.querySelector('.apex-spectrum__track');
      this.thumb = this.querySelector('.apex-spectrum__thumb');
      this.reset = this.querySelector('.apex-spectrum__reset');
      this.status = this.querySelector('.apex-spectrum__status');
      this.n = this.items.length;
      this.pos = null; // null = all

      // engage: show the bar, hide the no-JS swatch fallback
      this.removeAttribute('hidden');
      var fallback = document.querySelector('.apex-color-filter');
      if (fallback) fallback.setAttribute('hidden', '');

      this.track.addEventListener('pointerdown', this.onPointer.bind(this));
      this.thumb.addEventListener('keydown', this.onKey.bind(this));
      this.reset.addEventListener('click', this.clear.bind(this));
      this.render();
    }

    cards() {
      /* Task 35: also match the segmented hub template's custom grid
         cards (stethoscope-product-grid renders .product-card articles
         carrying data-colorway directly). */
      return Array.prototype.slice.call(
        document.querySelectorAll('#product-grid .grid__item, .apex-product-grid .product-card')
      );
    }

    onPointer(e) {
      e.preventDefault();
      this.track.setPointerCapture(e.pointerId);
      var move = this.fromEvent.bind(this);
      var up = function (ev) {
        this.track.releasePointerCapture(ev.pointerId);
        this.track.removeEventListener('pointermove', move);
        this.track.removeEventListener('pointerup', up);
      }.bind(this);
      this.track.addEventListener('pointermove', move);
      this.track.addEventListener('pointerup', up);
      this.fromEvent(e);
      /* no programmatic focus here — Safari treats it as
         focus-visible and draws the ring mid-drag (ED: thumb
         "gets all large" on first select) */
    }

    fromEvent(e) {
      var r = this.track.getBoundingClientRect();
      this.pos = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      this.render();
    }

    onKey(e) {
      var idx = this.nearestIndex();
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') idx += 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') idx -= 1;
      else if (e.key === 'Home') idx = 0;
      else if (e.key === 'End') idx = this.n - 1;
      else if (e.key === 'Escape') { this.clear(); return; }
      else return;
      e.preventDefault();
      idx = Math.min(this.n - 1, Math.max(0, idx));
      this.pos = (idx + 0.5) / this.n; // snap to the colorway center
      this.render();
    }

    nearestIndex() {
      if (this.pos === null) return 0;
      return Math.min(this.n - 1, Math.floor(this.pos * this.n));
    }

    visibleIds() {
      if (this.pos === null) return null; // all
      /* Task 37C: window widened to 1.5 slots so a typical park shows
         ~3-4 colorways. */
      var ids = [];
      for (var i = 0; i < this.n; i++) {
        var center = (i + 0.5) / this.n;
        if (Math.abs(center - this.pos) <= 1.5 / this.n) ids.push(this.items[i].id);
      }
      /* Task 37C guarantee: never zero results — fall back to the single
         nearest colorway (nearest slot center in the hue-ordered strip). */
      if (ids.length === 0) {
        var best = 0, bd = 2;
        for (var k = 0; k < this.n; k++) {
          var d = Math.abs((k + 0.5) / this.n - this.pos);
          if (d < bd) { bd = d; best = k; }
        }
        ids.push(this.items[best].id);
      }
      return ids;
    }

    render() {
      var ids = this.visibleIds();
      var names = [];
      if (ids === null) {
        this.thumb.style.left = '';
        this.thumb.classList.remove('is-set');
        this.reset.setAttribute('hidden', '');
      } else {
        this.thumb.style.left = (this.pos * 100).toFixed(2) + '%';
        this.thumb.classList.add('is-set');
        this.reset.removeAttribute('hidden');
        for (var i = 0; i < this.n; i++) {
          if (ids.indexOf(this.items[i].id) !== -1) names.push(this.items[i].name);
        }
      }
      this.cards().forEach(function (li) {
        /* Task 35: custom grid cards carry data-colorway on the card
           element itself; Dawn grid items carry it on a descendant. */
        var card = li.hasAttribute('data-colorway') ? li : li.querySelector('[data-colorway]');
        var id = card ? card.getAttribute('data-colorway') : null;
        var show = ids === null || (id && ids.indexOf(id) !== -1);
        li.style.display = show ? '' : 'none';
      });
      /* Task 35: on the segmented hub template, hide any grid section
         whose cards are all filtered out (header + empty grid + link).
         No-op on Dawn templates (no .apex-product-grid sections). */
      Array.prototype.forEach.call(
        document.querySelectorAll('.apex-product-grid'),
        function (sec) {
          var secCards = sec.querySelectorAll('.product-card');
          if (!secCards.length) return;
          var any = false;
          for (var j = 0; j < secCards.length; j++) {
            if (secCards[j].style.display !== 'none') { any = true; break; }
          }
          sec.style.display = any ? '' : 'none';
        }
      );
      var text = ids === null ? 'All colors' : 'Showing: ' + names.join(', ');
      this.status.textContent = text;
      this.thumb.setAttribute('aria-valuenow', String(this.nearestIndex()));
      this.thumb.setAttribute('aria-valuetext', text);
    }

    clear() {
      this.pos = null;
      this.render();
    }
  }

  customElements.define('apex-spectrum-bar', ApexSpectrumBar);
})();
